<template>
  <div class="incremental-onboarding-view-container">
    <div class="onboarding-view no-sidebar">
      <div class="top-fixed">
        <IncrementalOnboardingHeader
          :dismiss="dismiss" :current-step="0"
          :add-bank-account-view="true"
        />
      </div>
      <div class="s-flex s-gap-8 s-p-6 s-py-0">
        <div class="s-p-4 s-fixed s-top-4" style="width: 256px; margin-top: 10px;">
          <ProgressSidebar
            :steps="steps"
            :current-step-index="currentStep"
            :completed-until-step="completedUntilStep"
            @update:currentStep="(i) => currentStep = i"
            :disable-clicks="true"
          />
        </div>
        <!-- Main Content -->
        <div class="s-flex-1 s-p-6" style="margin-left: 350px; margin-top:10px; padding-bottom: 300px; max-width: 600px">
          <ownership-overview
            v-if="isBeneficiaryStep && currentStep === stepIndexMap['ownership-overview']"
            @modified="completedUntilStep = stepIndexMap['ownership-overview'] - 1"
            @proceed="goNext"
            @previous="goBack"
            :is-update="isUpdate"
            :error-codes="controlPersonErrorCodes"
          />
          <business-registration
            v-if="isBusinessRegistrationStep && currentStep === stepIndexMap['business-registration']"
            @modified="completedUntilStep = stepIndexMap['business-registration'] - 1"
            @proceed="goNext"
            @previous="goBack"
            :is-update="isUpdate"
            :error-codes="businessRegistrationErrorCodes"
          />
          <incremental-onboarding-business-profile
            v-if="isUpdateBusinessStep && currentStep === stepIndexMap['incremental-onboarding-business-profile']"
            @modified="completedUntilStep = stepIndexMap['incremental-onboarding-business-profile'] - 1"
            @proceed="goNext"
            @previous="goBack"
            :is-update="isUpdate"
            :error-codes="businessProfileErrorCodes"
          />
          <incremental-onboarding-bank-account-for-payout
            v-if="isBankAccountStep && currentStep === stepIndexMap['incremental-onboarding-bank-account-for-payout']"
            @modified="completedUntilStep = stepIndexMap['incremental-onboarding-bank-account-for-payout'] - 1"
            @proceed="goNext"
            @previous="goBack"
            :is-update="isUpdate"
            :error-codes="bankErrorCodes"
          />
          <incremental-document-upload
            v-if="isUploadDocumentStep && currentStep === stepIndexMap['upload-document']"
            :failure-reason-codes="getDocumentUploadErrorCodes"
            @modified="completedUntilStep = stepIndexMap['upload-document'] - 1"
            @proceed="goNext"
            @previous="goBack"
            :error-codes="documentUploadErrorCodes"
          />
        </div>
      </div>
      <IncrementalFooter />
    </div>
  </div>
</template>

<script>
import IncrementalOnboardingHeader from "@/components/onboarding/IncrementalOnboardingHeader.vue";
import ProgressSidebar from "@/components/incremental-onboarding/ProgressSidebar.vue";
import IncrementalFooter from "@/components/incremental-onboarding/IncrementalFooter.vue";
import IncrementalOnboardingBankAccountForPayout
  from "@/components/incremental-onboarding/IncrementalOnboardingBankAccountForPayout.vue";
import BusinessRegistration from "@/components/incremental-onboarding/BusinessRegistration.vue";
import { editOnboardingMixin } from "@/mixin/editOnboardingMixin";
import { callbackApplication } from "@/app/utils/common/functions/functions";
import IncrementalDocumentUpload from "@/components/incremental-onboarding/IncrementalDocumentUpload.vue";
import OwnershipOverview from "@/components/incremental-onboarding/OwnershipOverview.vue";
import IncrementalOnboardingBusinessProfile
  from "@/components/incremental-onboarding/IncrementalOnboardingBusinessProfile.vue";
import { fieldErrorMapping } from "@/app/utils/common/functions/payfac-error-code-mapper/adyen/field-error-mapping";
import { navigateTo } from "@/router";
import { RouteConstants } from "@/router/routeConstants";

export default {
  name: "IncrementalEditView",
  components: {
    IncrementalOnboardingBusinessProfile,
    OwnershipOverview,
    IncrementalDocumentUpload,
    BusinessRegistration,
    IncrementalOnboardingBankAccountForPayout,
    IncrementalFooter,
    ProgressSidebar,
    IncrementalOnboardingHeader,
  },
  mixins: [editOnboardingMixin],
  data() {
    return {
      isUpdate: true,
      orderedStepIds: [
        "ownership-overview",
        "business-registration",
        "incremental-onboarding-business-profile",
        "incremental-onboarding-bank-account-for-payout",
        "upload-document",
        // "some-new-step-id",
      ],
      stepMeta: {
        "ownership-overview": {
          label: "Update Beneficial Owner",
          description: "Add a beneficial owner to your business.",
        },
        "business-registration": {
          label: "Business Registration",
          description: "Register your business.",
        },
        "incremental-onboarding-business-profile": {
          label: "Business Profile",
          description: "Update your business profile.",
        },
        "incremental-onboarding-bank-account-for-payout": {
          label: "Business Bank Account",
          description: "Optional: Add a bank account.",
        },
        "upload-document": {
          label: "Upload Document",
          description: "Upload necessary documents.",
        }
        // "some-new-step-id": { label: "…", description: "…" }
      },
      steps: [],
      stepIndexMap: {},
      currentStep: 0,
      completedUntilStep: -1,
      bankErrorCodes: [],
      businessRegistrationErrorCodes: [],
      businessProfileErrorCodes: [],
      controlPersonErrorCodes: [],
      documentUploadErrorCodes: [],
    };
  },
  computed: {
    isBusinessRegistrationStep() {
      return this.getBusinessRegistrationErrorCodes !== null && this.getBusinessRegistrationErrorCodes.length > 0;
    },
    isBankAccountStep() {
      return this.getBankRelatedErrorCodes !== null && this.getBankRelatedErrorCodes.length > 0;
    },
    isUploadDocumentStep() {
      return this.getDocumentUploadErrorCodes !== null && this.getDocumentUploadErrorCodes.length > 0
    },
    isBeneficiaryStep() {
      return this.getControlPersonErrorCodes !==null && this.getControlPersonErrorCodes.length > 0;
    },
    isUpdateBusinessStep() {
      return this.getBusinessProfileErrorCodes !== null && this.getBusinessProfileErrorCodes.length > 0;
    },
  },
  created() {
    this.buildSteps();
    this.computeStepPositions();
    this.getErrorCodes.forEach(errorCode => {
      const rawErrorCode = this.getPayfacErrorCodeMapper.getRawErrorCode(errorCode);
      if (this.getBankRelatedErrorCodes.includes(errorCode)) {
        const errorMsg = {
          key: fieldErrorMapping.ADYEN.BANK_ACCOUNT[rawErrorCode].field,
          description: fieldErrorMapping.ADYEN.BANK_ACCOUNT[rawErrorCode].description,
        };
        this.bankErrorCodes.push(errorMsg);
      }
      if (this.getBusinessRegistrationErrorCodes.includes(errorCode)) {
        const errorMsg = {
          key: fieldErrorMapping.ADYEN.BUSINESS_REGISTRATION[rawErrorCode].field,
          description: fieldErrorMapping.ADYEN.BUSINESS_REGISTRATION[rawErrorCode].description,
        };
        this.businessRegistrationErrorCodes.push(errorMsg);
      }
      if (this.getBusinessProfileErrorCodes.includes(errorCode)) {
        const errorMsg = {
          key: fieldErrorMapping.ADYEN.BUSINESS_PROFILE[rawErrorCode].field,
          description: fieldErrorMapping.ADYEN.BUSINESS_PROFILE[rawErrorCode].description,
        };
        this.businessProfileErrorCodes.push(errorMsg);
      }
      if (this.getControlPersonErrorCodes.includes(errorCode)) {
        const errorMsg = {
          key: fieldErrorMapping.ADYEN.CONTROL_PERSON[rawErrorCode].field,
          description: fieldErrorMapping.ADYEN.CONTROL_PERSON[rawErrorCode].description,
        };
        this.controlPersonErrorCodes.push(errorMsg);
      }
      if (this.getDocumentUploadErrorCodes.includes(errorCode)) {
        const errorMsg = {
          key: fieldErrorMapping.ADYEN.DOCUMENT_UPLOAD[rawErrorCode].field,
          description: fieldErrorMapping.ADYEN.DOCUMENT_UPLOAD[rawErrorCode].description,
        };
        this.documentUploadErrorCodes.push(errorMsg);
      }
    });
  },
  methods: {
    ...editOnboardingMixin,
    buildSteps() {
      const visibleSteps = this.orderedStepIds.filter((id) => {
        switch (id) {
          case "ownership-overview":
            return this.isBeneficiaryStep;
          case "business-registration":
            return this.isBusinessRegistrationStep;
          case "incremental-onboarding-business-profile":
            return this.isUpdateBusinessStep;
          case "incremental-onboarding-bank-account-for-payout":
            return this.isBankAccountStep;
          case "upload-document":
            return this.isUploadDocumentStep;
          // case "some-new-step-id": return this.isSomeNewStep;
          default:
            return false;
        }
      });

      this.steps = visibleSteps.map((id) => ({ id, ...this.stepMeta[id] }));

      // Build a quick lookup map → { "business-registration": 0, … }
      this.stepIndexMap = this.steps.reduce((map, { id }, idx) => {
        map[id] = idx;
        return map;
      }, {});
    },
    computeStepPositions() {
      // If *no* steps are present we keep 0/‑1 and let the UI show a "done" state.
      if (!this.steps.length) return;

      this.currentStep = 0;                 // First visible step is always index 0
      this.completedUntilStep = -1;         // Nothing completed before the first step
    },
    dismiss() {
      navigateTo(RouteConstants.DASHBOARD);
    },
    goBack() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    goNext() {
      if (this.currentStep < this.steps.length - 1) {
        // Normal forward movement
        this.completedUntilStep = Math.max(
          this.currentStep,
          this.completedUntilStep,
        );
        this.currentStep++;
      } else {
        this.$success("Updating completed successfully!")
        setTimeout(() => {
          callbackApplication(
            this.getCallbackUrl,
            this.getMerchantId,
            undefined
          );
        }, 5000);
      }
    },
  },
};
</script>